import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from '../firebase/firebase';
import api from './config';
import { SafeDatabaseUser } from '../types/types';

const USER_API_URL = `${process.env.REACT_APP_SERVER_URL}/user`;

// Maximum file size in bytes (5 MB)
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Checks if an image file is valid
 * @param file The file to check
 * @returns An object with validation result
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File must be an image' };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds the ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`,
    };
  }

  return { valid: true };
};

/**
 * Compresses an image file
 * @param file The image file to compress
 * @param maxWidth Maximum width of the output image
 * @param quality Quality of the output image (0-1)
 * @returns A promise that resolves to a compressed file
 */
export const compressImage = (file: File, maxWidth = 800, quality = 0.8): Promise<File> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = event => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const { width, height } = img;

        // Calculate new dimensions
        let newWidth = width;
        let newHeight = height;
        if (width > maxWidth) {
          newHeight = Math.round((height * maxWidth) / width);
          newWidth = maxWidth;
        }

        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            // Create a new file with the compressed blob
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality,
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });

/**
 * Uploads a profile picture to Firebase Storage
 * @param username The username of the user
 * @param file The image file to upload
 * @param onProgress Optional callback to report upload progress
 * @returns A promise that resolves to the download URL
 */
export const uploadProfilePicture = async (
  username: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<string> => {
  try {
    // Validate the file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid file');
    }

    const compressedFile = await compressImage(file);

    const storageRef = ref(storage, `profile_pictures/${username}_${Date.now()}`);

    const uploadTask = uploadBytesResumable(storageRef, compressedFile);

    const downloadURL = await new Promise<string>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot: UploadTaskSnapshot) => {
          if (onProgress) {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress);
          }
        },
        error => {
          reject(error);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (error) {
            reject(error);
          }
        },
      );
    });

    return downloadURL;
  } catch (error) {
    throw new Error(`Error uploading profile picture: ${(error as Error).message}`);
  }
};

/**
 * Updates the user's profile picture URL in the database
 * @param username The username of the user
 * @param profilePictureUrl The URL of the uploaded profile picture
 * @returns A promise that resolves to the updated user data
 */
export const updateProfilePicture = async (
  username: string,
  profilePictureUrl: string,
): Promise<SafeDatabaseUser> => {
  try {
    const res = await api.put(`${USER_API_URL}/updateProfilePicture`, {
      username,
      profilePicture: profilePictureUrl,
    });

    if (res.status !== 200) {
      throw new Error('Error when updating profile picture');
    }

    return res.data;
  } catch (error) {
    throw new Error(`Error updating profile picture: ${(error as Error).message}`);
  }
};

/**
 * Deletes a profile picture from Firebase Storage
 * @param pictureUrl The URL of the profile picture to delete
 * @returns A promise that resolves when the operation is complete
 */
export const deleteProfilePicture = async (pictureUrl: string): Promise<void> => {
  try {
    // Create a reference to the storage location from the URL
    const storageRef = ref(storage, pictureUrl);

    // Delete the file
    await deleteObject(storageRef);
  } catch (error) {
    throw new Error(`Error deleting profile picture: ${(error as Error).message}`);
  }
};
